import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from "../ui/button";
import { Camera, X, AlertTriangle } from 'lucide-react';
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { useTheme } from '../ui/theme-provider';
import { LoadingSpinner } from '../ui/loading-spinner';

let cv = null; // OpenCV.js instance

const SmartReceiptScanner = ({ 
  onCaptureComplete, 
  onClose, 
  isOpen = false 
}) => {
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);
  const [edgeDetected, setEdgeDetected] = useState(false);
  const [detectedCorners, setDetectedCorners] = useState(null);
  const [detectionLocked, setDetectionLocked] = useState(false);
  const [lockStartTime, setLockStartTime] = useState(null);
  const [detectionHistory, setDetectionHistory] = useState([]);
  const [stableDetection, setStableDetection] = useState(null);
  const [distanceGuidance, setDistanceGuidance] = useState('');
  const [stream, setStream] = useState(null);
  
  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const processingIntervalRef = useRef(null);
  
  // Add refs to prevent multiple simultaneous captures
  const captureInProgress = useRef(false);
  const lastCaptureTime = useRef(0);
  
  const { toast } = useToast();
  const { theme } = useTheme();

  // Stability settings
  const STABILITY_THRESHOLD = 8;
  const LOCK_DURATION = 4000;
  const DETECTION_TOLERANCE = 50;
  const MIN_DETECTION_AREA = 15000;
  const MIN_CAPTURE_INTERVAL = 1000; // Prevent captures within 1 second of each other

  // Load OpenCV.js
  const loadOpenCV = useCallback(() => {
    if (cv) {
      setLoading(false);
      return;
    }

    if (document.querySelector('script[src*="opencv.js"]')) {
      const checkInterval = setInterval(() => {
        if (window.cv && window.cv.Mat) {
          cv = window.cv;
          clearInterval(checkInterval);
          setLoading(false);
        }
      }, 100);
      
      setTimeout(() => {
        if (!cv) {
          clearInterval(checkInterval);
          setError('OpenCV loading timeout');
          setLoading(false);
        }
      }, 5000);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://docs.opencv.org/4.8.0/opencv.js';
    script.async = true;
    
    script.onload = () => {
      const checkInterval = setInterval(() => {
        if (window.cv && window.cv.Mat) {
          cv = window.cv;
          clearInterval(checkInterval);
          setLoading(false);
        }
      }, 100);
    };
    
    script.onerror = () => {
      setError('Smart scan features failed to load. Using basic camera.');
      setLoading(false);
    };
    
    document.head.appendChild(script);
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
      } catch (backCameraError) {
        mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      }

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Camera access denied. Please check browser permissions.');
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive"
      });
    }
  }, [toast]);

  // Helper functions
  const areDetectionsSimilar = (detection1, detection2, tolerance = DETECTION_TOLERANCE) => {
    if (!detection1 || !detection2) return false;
    if (detection1.length !== 4 || detection2.length !== 4) return false;
    
    for (let i = 0; i < 4; i++) {
      const dx = Math.abs(detection1[i].x - detection2[i].x);
      const dy = Math.abs(detection1[i].y - detection2[i].y);
      if (dx > tolerance || dy > tolerance) {
        return false;
      }
    }
    return true;
  };

  const isValidRectangle = useCallback((corners, width, height) => {
    if (corners.length !== 4) return false;
    
    const inBounds = corners.every(corner => 
      corner.x >= 0 && corner.x <= width && 
      corner.y >= 0 && corner.y <= height
    );
    
    if (!inBounds) return false;
    
    let area = 0;
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4;
      area += corners[i].x * corners[j].y;
      area -= corners[j].x * corners[i].y;
    }
    area = Math.abs(area) / 2;
    
    const minArea = width * height * 0.1;
    const maxArea = width * height * 0.8;
    
    return area >= minArea && area <= maxArea;
  }, []);

  // Distance guidance based on detection size
  const calculateDistanceGuidance = (corners, videoWidth, videoHeight) => {
    if (!corners || corners.length !== 4) return '';
    
    // Calculate the area of detected rectangle
    let area = 0;
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4;
      area += corners[i].x * corners[j].y;
      area -= corners[j].x * corners[i].y;
    }
    area = Math.abs(area) / 2;
    
    const totalArea = videoWidth * videoHeight;
    const areaRatio = area / totalArea;
    
    if (areaRatio < 0.15) {
      return 'Move closer to the receipt';
    } else if (areaRatio > 0.7) {
      return 'Move back from the receipt';
    } else if (areaRatio >= 0.25 && areaRatio <= 0.6) {
      return 'Perfect distance - hold steady';
    } else {
      return 'Adjust distance slightly';
    }
  };

  const getBoundingBox = (corners) => {
    const xs = corners.map(c => c.x);
    const ys = corners.map(c => c.y);
    return {
      x: Math.min(...xs),
      y: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys)
    };
  };

  const createCleanCrop = (sourceCanvas, boundingBox) => {
    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = Math.round(boundingBox.width);
    croppedCanvas.height = Math.round(boundingBox.height);
    
    const croppedCtx = croppedCanvas.getContext('2d');
    
    croppedCtx.fillStyle = '#FFFFFF';
    croppedCtx.fillRect(0, 0, croppedCanvas.width, croppedCanvas.height);
    
    croppedCtx.globalCompositeOperation = 'source-over';
    croppedCtx.drawImage(
      sourceCanvas,
      Math.round(boundingBox.x),
      Math.round(boundingBox.y),
      Math.round(boundingBox.width),
      Math.round(boundingBox.height),
      0,
      0,
      croppedCanvas.width,
      croppedCanvas.height
    );
    
    return croppedCanvas;
  };

  // Process frame function
  const processFrame = useCallback(() => {
    if (!videoRef.current || !overlayCanvasRef.current || videoRef.current.paused) {
      return;
    }

    const video = videoRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    
    if (video.videoWidth === 0) return;

    overlayCanvas.width = video.videoWidth;
    overlayCanvas.height = video.videoHeight;
    const overlayCtx = overlayCanvas.getContext('2d');
    
    overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    overlayCtx.fillStyle = 'rgba(0, 0, 0, 0)';
    overlayCtx.fillRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    
    const currentTime = Date.now();
    
    // Check if detection is locked
    if (detectionLocked && lockStartTime) {
      if (currentTime - lockStartTime < LOCK_DURATION) {
        if (stableDetection) {
          drawDetectedRectangle(overlayCtx, stableDetection, true);
          setDistanceGuidance('Locked! Ready to capture');
        }
        return;
      } else {
        setDetectionLocked(false);
        setLockStartTime(null);
        setStableDetection(null);
        setDetectedCorners(null);
        setDetectionHistory([]);
        setEdgeDetected(false);
        setDistanceGuidance('');
      }
    }

    if (cv && cv.Mat) {
      try {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = video.videoWidth;
        tempCanvas.height = video.videoHeight;
        const tempCtx = tempCanvas.getContext('2d');
        
        tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
        tempCtx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
        
        const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const src = cv.matFromImageData(imageData);
        const gray = new cv.Mat();
        const edges = new cv.Mat();
        const contours = new cv.MatVector();
        const hierarchy = new cv.Mat();

        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
        cv.GaussianBlur(gray, gray, new cv.Size(7, 7), 0);
        cv.Canny(gray, edges, 40, 120);
        cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
        
        let bestRect = null;
        let maxArea = MIN_DETECTION_AREA;
        
        for (let i = 0; i < contours.size(); i++) {
          const contour = contours.get(i);
          const area = cv.contourArea(contour);
          
          if (area > maxArea) {
            const epsilon = 0.015 * cv.arcLength(contour, true);
            const approx = new cv.Mat();
            cv.approxPolyDP(contour, approx, epsilon, true);
            
            if (approx.rows === 4) {
              const corners = [];
              for (let j = 0; j < 4; j++) {
                corners.push({
                  x: approx.data32S[j * 2],
                  y: approx.data32S[j * 2 + 1]
                });
              }
              
              if (isValidRectangle(corners, video.videoWidth, video.videoHeight)) {
                maxArea = area;
                bestRect = corners;
              }
            }
            approx.delete();
          }
          contour.delete();
        }
        
        // Update distance guidance
        if (bestRect) {
          const guidance = calculateDistanceGuidance(bestRect, video.videoWidth, video.videoHeight);
          setDistanceGuidance(guidance);
        } else {
          setDistanceGuidance('Point camera at receipt');
        }
        
        // Update detection history
        setDetectionHistory(prev => {
          const newHistory = [...prev, { 
            detected: !!bestRect, 
            corners: bestRect, 
            time: currentTime 
          }].slice(-10);
          
          const recentDetections = newHistory.filter(d => currentTime - d.time < 2000);
          const validDetections = recentDetections.filter(d => d.detected);
          
          let stableCount = 0;
          let candidateDetection = null;
          
          if (validDetections.length >= 3) {
            const lastDetection = validDetections[validDetections.length - 1];
            candidateDetection = lastDetection.corners;
            
            for (let i = validDetections.length - 1; i >= Math.max(0, validDetections.length - STABILITY_THRESHOLD); i--) {
              if (areDetectionsSimilar(candidateDetection, validDetections[i].corners)) {
                stableCount++;
              } else {
                break;
              }
            }
          }
          
          if (stableCount >= STABILITY_THRESHOLD && candidateDetection && !detectionLocked) {
            setDetectionLocked(true);
            setLockStartTime(currentTime);
            setStableDetection(candidateDetection);
            setDetectedCorners(candidateDetection);
            setEdgeDetected(true);
            setDistanceGuidance('Perfect! Hold steady');
            
            if (navigator.vibrate) {
              navigator.vibrate([50, 30, 50]);
            }
          } else if (validDetections.length < 2) {
            setEdgeDetected(false);
            if (!detectionLocked) {
              setDetectedCorners(null);
            }
          } else {
            setEdgeDetected(true);
            if (!detectionLocked) {
              setDetectedCorners(bestRect);
            }
          }
          
          return newHistory;
        });
        
        if (!detectionLocked) {
          if (bestRect) {
            drawDetectedRectangle(overlayCtx, bestRect, false);
          } else {
            drawAdaptiveGuide(overlayCtx, video.videoWidth, video.videoHeight);
          }
        }
        
        // Cleanup OpenCV objects
        src.delete();
        gray.delete();
        edges.delete();
        contours.delete();
        hierarchy.delete();
        
      } catch (err) {
        console.error('OpenCV processing error:', err);
        if (!detectionLocked) {
          setEdgeDetected(false);
          setDetectedCorners(null);
          setDistanceGuidance('Basic camera mode');
          drawAdaptiveGuide(overlayCtx, video.videoWidth, video.videoHeight);
        }
      }
    } else {
      if (!detectionLocked) {
        setDistanceGuidance('Loading smart features...');
        drawAdaptiveGuide(overlayCtx, video.videoWidth, video.videoHeight);
      }
    }
  }, [isValidRectangle, detectionLocked, lockStartTime, stableDetection]);

  // Drawing functions
  const drawDetectedRectangle = (ctx, corners, isLocked) => {
    if (!corners || corners.length !== 4) return;
    
    const color = isLocked ? '#00ff00' : '#ffff00';
    const lineWidth = isLocked ? 5 : 3;
    const shadowBlur = isLocked ? 10 : 5;
    
    ctx.shadowColor = color;
    ctx.shadowBlur = shadowBlur;
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      ctx.lineTo(corners[i].x, corners[i].y);
    }
    ctx.closePath();
    ctx.stroke();
    
    ctx.shadowBlur = 0;
    
    ctx.fillStyle = color;
    corners.forEach(corner => {
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, isLocked ? 12 : 10, 0, 2 * Math.PI);
      ctx.fill();
    });
    
    if (isLocked) {
      ctx.fillStyle = 'rgba(0, 255, 0, 0.1)';
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      
      ctx.fillStyle = '#00ff00';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('LOCKED', ctx.canvas.width / 2, 50);
    }
  };

  const drawAdaptiveGuide = (ctx, width, height) => {
    ctx.strokeStyle = '#ff6b6b';
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 5]);
    
    const aspectRatio = width / height;
    let guideWidth, guideHeight;
    
    if (aspectRatio > 1.5) {
      guideWidth = width * 0.8;
      guideHeight = height * 0.6;
    } else {
      guideWidth = width * 0.7;
      guideHeight = height * 0.8;
    }
    
    const centerX = width / 2;
    const centerY = height / 2;
    
    ctx.strokeRect(
      centerX - guideWidth / 2,
      centerY - guideHeight / 2,
      guideWidth,
      guideHeight
    );
    
    const cornerSize = 20;
    const corners = [
      { x: centerX - guideWidth / 2, y: centerY - guideHeight / 2 },
      { x: centerX + guideWidth / 2, y: centerY - guideHeight / 2 },
      { x: centerX + guideWidth / 2, y: centerY + guideHeight / 2 },
      { x: centerX - guideWidth / 2, y: centerY + guideHeight / 2 }
    ];
    
    ctx.setLineDash([]);
    ctx.lineWidth = 3;
    
    corners.forEach(corner => {
      ctx.beginPath();
      ctx.moveTo(corner.x - cornerSize, corner.y);
      ctx.lineTo(corner.x, corner.y);
      ctx.lineTo(corner.x, corner.y - cornerSize);
      ctx.stroke();
    });
  };

  // SINGLE capturePhoto function - no duplicates
  const capturePhoto = async () => {
    if (!videoRef.current) return;
    
    // Prevent multiple simultaneous captures
    const currentTime = Date.now();
    if (captureInProgress.current || (currentTime - lastCaptureTime.current) < MIN_CAPTURE_INTERVAL) {
      console.log('Capture blocked - too soon or already in progress');
      return;
    }
    
    captureInProgress.current = true;
    lastCaptureTime.current = currentTime;
    setCapturing(true);
    
    try {
      const video = videoRef.current;
      
      // Stop processing during capture
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
        processingIntervalRef.current = null;
      }
      
      // Clear overlay canvas
      if (overlayCanvasRef.current) {
        const overlayCtx = overlayCanvasRef.current.getContext('2d');
        overlayCtx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
      }
      
      // Wait for stable video frame
      await new Promise(resolve => {
        let frameCount = 0;
        const waitForFrames = () => {
          frameCount++;
          if (frameCount >= 5) {
            resolve();
          } else {
            requestAnimationFrame(waitForFrames);
          }
        };
        requestAnimationFrame(waitForFrames);
      });
      
      // Create single capture canvas
      const captureCanvas = document.createElement('canvas');
      captureCanvas.width = video.videoWidth;
      captureCanvas.height = video.videoHeight;
      const captureCtx = captureCanvas.getContext('2d');
      
      // Clear canvas completely
      captureCtx.save();
      captureCtx.setTransform(1, 0, 0, 1, 0, 0);
      captureCtx.clearRect(0, 0, captureCanvas.width, captureCanvas.height);
      captureCtx.fillStyle = '#FFFFFF'; // White background
      captureCtx.fillRect(0, 0, captureCanvas.width, captureCanvas.height);
      captureCtx.restore();
      
      // Draw single clean frame
      captureCtx.globalCompositeOperation = 'source-over';
      captureCtx.globalAlpha = 1.0;
      captureCtx.drawImage(video, 0, 0, captureCanvas.width, captureCanvas.height);
      
      let finalCanvas = captureCanvas;
      const cornersToUse = stableDetection || detectedCorners;
      
      // Use simple bounding box crop if detection is locked
      if (detectionLocked && cornersToUse && cornersToUse.length === 4) {
        try {
          const boundingBox = getBoundingBox(cornersToUse);
          
          // Add padding to ensure we don't cut off edges
          const padding = 10;
          boundingBox.x = Math.max(0, boundingBox.x - padding);
          boundingBox.y = Math.max(0, boundingBox.y - padding);
          boundingBox.width = Math.min(captureCanvas.width - boundingBox.x, boundingBox.width + (padding * 2));
          boundingBox.height = Math.min(captureCanvas.height - boundingBox.y, boundingBox.height + (padding * 2));
          
          finalCanvas = createCleanCrop(captureCanvas, boundingBox);
          
        } catch (cropError) {
          console.warn('Simple cropping failed, using full image:', cropError);
          finalCanvas = captureCanvas;
        }
      }
      
      // Single blob conversion
      const blobPromise = new Promise((resolve, reject) => {
        finalCanvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        }, 'image/jpeg', 0.95);
      });
      
      const blob = await blobPromise;
      
      if (blob) {
        const filename = detectionLocked ? `smart-receipt-${Date.now()}.jpg` : `receipt-${Date.now()}.jpg`;
        const file = new File([blob], filename, { type: 'image/jpeg' });
        
        if (onCaptureComplete) {
          await onCaptureComplete(file);
        }
      }
      
      // Close scanner
      handleClose();
      
    } catch (err) {
      console.error('Capture error:', err);
      toast({
        title: "Capture Failed",
        description: `Failed to capture image: ${err.message}. Please try again.`,
        variant: "destructive"
      });
    } finally {
      setCapturing(false);
      captureInProgress.current = false;
    }
  };

  // Enhanced handleClose function
  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
      processingIntervalRef.current = null;
    }
    
    setLoading(true);
    setError(null);
    setEdgeDetected(false);
    setDetectedCorners(null);
    setDetectionLocked(false);
    setLockStartTime(null);
    setStableDetection(null);
    setDetectionHistory([]);
    setDistanceGuidance('');
    setCapturing(false);
    
    captureInProgress.current = false;
    lastCaptureTime.current = 0;
    
    if (overlayCanvasRef.current) {
      const overlayCtx = overlayCanvasRef.current.getContext('2d');
      overlayCtx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
    }
    
    if (onClose) {
      onClose();
    }
  };

  // Effects
  useEffect(() => {
    if (isOpen) {
      // Reset states when opening
      setLoading(true);
      setError(null);
      setEdgeDetected(false);
      setDetectedCorners(null);
      setDetectionLocked(false);
      setLockStartTime(null);
      setStableDetection(null);
      setDetectionHistory([]);
      setDistanceGuidance('');
      setCapturing(false);
      captureInProgress.current = false;
      lastCaptureTime.current = 0;
      
      // Start camera and OpenCV
      loadOpenCV();
      startCamera();
    }

    return () => {
      // Cleanup when closing
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
        processingIntervalRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || loading || error) return;
    
    processingIntervalRef.current = setInterval(() => {
      processFrame();
    }, 200);
    
    return () => {
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
        processingIntervalRef.current = null;
      }
    };
  }, [isOpen, loading, error]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
      <div className="relative w-full h-full flex flex-col">
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 z-20 flex justify-between items-center">
          <div className={cn(
            "px-3 py-1 rounded-full text-sm font-medium transition-colors",
            loading ? "bg-gray-500/20 text-gray-300" :
            error ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" :
            detectionLocked ? "bg-green-500/20 text-green-400 border border-green-500/30 animate-pulse" :
            edgeDetected ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" : 
            "bg-blue-500/20 text-blue-400 border border-blue-500/30"
          )}>
            {loading ? "Starting Scanner..." : 
             error ? "Basic Camera Mode" :
             detectionLocked ? `Receipt Locked! (${Math.ceil((LOCK_DURATION - (Date.now() - (lockStartTime || 0))) / 1000)}s)` :
             edgeDetected ? "Hold steady... detecting edges..." : 
             distanceGuidance || "Position receipt in frame"}
          </div>
          
          <Button variant="ghost" size="icon" onClick={handleClose} className="bg-black/50 hover:bg-black/70 text-white rounded-full h-9 w-9">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Camera View */}
        <div className="flex-1 relative overflow-hidden bg-black flex items-center justify-center">
          {/* Loading/Error overlay */}
          {(loading || error) && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/70">
              <div className="text-center text-white max-w-xs p-4">
                {loading ? (
                  <>
                    <LoadingSpinner size="large" />
                    <p className="mt-2">Starting camera...</p>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-12 w-12 mx-auto mb-2 text-yellow-400" />
                    <p className="mt-2">{error}</p>
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Video feed */}
          <video 
            ref={videoRef} 
            className="w-full h-full object-cover" 
            style={{
              minHeight: '100%',
              minWidth: '100%',
              objectFit: 'cover'
            }}
            playsInline 
            muted 
            autoPlay 
          />
          
          {/* Edge detection overlay */}
          <canvas 
            ref={overlayCanvasRef} 
            className="absolute inset-0 w-full h-full pointer-events-none z-10" 
            style={{
              objectFit: 'cover'
            }}
          />
          
          {/* Dark background prompt */}
          {!loading && !error && !detectionLocked && !edgeDetected && (
            <div className="absolute bottom-32 left-4 right-4 bg-black/60 rounded-lg p-3 text-center z-10">
              <div className="text-white text-sm font-medium mb-1">
                <span className="text-yellow-400">Tip:</span> Dark backgrounds work best
              </div>
              <p className="text-xs text-white/80">
                Good lighting, flat receipt, and solid contrast help with detection
              </p>
            </div>
          )}
        </div>

        {/* Instructions and Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/60 to-transparent z-20">
          <div className="flex flex-col items-center space-y-4">
            {!loading && !error && (
              <p className="text-white text-sm text-center max-w-md">
                {detectionLocked 
                  ? "Perfect! The app will crop to the detected area automatically." 
                  : distanceGuidance 
                  ? `${distanceGuidance}. Hold steady for auto-detection.`
                  : "Position your receipt within the frame. The scanner will detect the edges automatically."}
              </p>
            )}
            
            <Button 
              onClick={capturePhoto} 
              disabled={capturing || loading} 
              className={cn(
                "w-20 h-20 rounded-full text-white border-4 transition-all duration-300 flex items-center justify-center",
                capturing ? "bg-gray-500 border-gray-400" :
                detectionLocked ? "bg-green-500 hover:bg-green-600 border-green-300 scale-110 shadow-lg shadow-green-500/50 animate-pulse" : 
                "bg-white/20 hover:bg-white/30 border-white/40"
              )}
            >
              {capturing ? (
                <LoadingSpinner size="small" />
              ) : (
                <Camera className="h-10 w-10" />
              )}
            </Button>
            
            {capturing && (
              <p className="text-white text-xs">Processing capture...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartReceiptScanner;